import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicTrace() {
  const [params] = useSearchParams();
  const [input, setInput] = useState(params.get("fg") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchTrace = async (batchNo) => {
    if (!batchNo) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await fetch(`${API}/api/public/trace/${encodeURIComponent(batchNo)}`);
      if (res.status === 404) {
        setError(`No FG batch found for "${batchNo}".`);
      } else if (!res.ok) {
        setError(`Lookup failed (HTTP ${res.status}).`);
      } else {
        setData(await res.json());
      }
    } catch (e) {
      setError("Network error — please try again.");
    }
    setLoading(false);
  };

  // Auto-fetch when arriving via QR scan with ?fg=...
  useEffect(() => {
    const fg = params.get("fg");
    if (fg) fetchTrace(fg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchTrace(input.trim());
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#1A3560] text-white">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" data-testid="public-trace-title">
              AMPL — Product Traceability
            </h1>
            <p className="text-xs text-blue-200 mt-0.5">
              Public lookup · No login required
            </p>
          </div>
          <div className="text-xs text-blue-200 text-right hidden sm:block">
            Ajay Merumetals Pvt. Ltd.<br />
            IATF 16949 traceable lineage
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Search box */}
        <form onSubmit={onSubmit} className="bg-white rounded-lg shadow p-4 mb-4 flex gap-2">
          <input
            data-testid="public-trace-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter FG Batch Number (e.g., 20260106-1-L1-260108-L1-260112)"
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
          />
          <button
            type="submit"
            data-testid="public-trace-submit"
            disabled={loading || !input.trim()}
            className="bg-[#1A3560] text-white px-4 py-2 rounded text-sm font-semibold hover:bg-[#142847] disabled:opacity-50"
          >
            {loading ? "Looking up..." : "Trace"}
          </button>
        </form>

        {error && (
          <div
            data-testid="public-trace-error"
            className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm mb-4"
          >
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-slate-500 text-sm">Loading lineage…</div>
        )}

        {data && <TraceCard data={data} />}

        {!data && !error && !loading && !params.get("fg") && (
          <div className="bg-white rounded-lg shadow p-6 text-sm text-slate-600">
            <p className="font-semibold text-slate-800 mb-2">How to use this page</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Scan the QR code printed on the carton, or paste the FG Batch Number above.</li>
              <li>You'll see the complete product lineage: raw material source, every operation performed, and final batch details.</li>
              <li>This page is read-only and intended for customers and auditors.</li>
            </ul>
          </div>
        )}
      </div>

      <div className="text-center text-xs text-slate-400 pb-6">
        For any discrepancy, contact quality@ajaymerumetals.com
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-800">
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </div>
    </div>
  );
}

function TraceCard({ data }) {
  const rm = data.rm || {};
  const statusColor =
    data.status === "READY"
      ? "bg-emerald-100 text-emerald-700"
      : data.status === "DISPATCHED"
      ? "bg-blue-100 text-blue-700"
      : "bg-slate-100 text-slate-700";

  return (
    <div className="space-y-4" data-testid="public-trace-result">
      {/* Top summary */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">FG Batch No</div>
            <div className="text-lg font-bold text-[#1A3560] break-all" data-testid="public-trace-fg-no">
              {data.fg_batch_no}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor}`}>
            {data.status || "—"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Part No" value={data.part_no} />
          <Field label="Part Name" value={data.part_name} />
          <Field label="Quantity" value={data.qty} />
          <Field label="Created" value={(data.created_at || "").split("T")[0]} />
        </div>
      </div>

      {/* RM source */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">
            RM
          </span>
          Raw Material Source
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Batch Code" value={data.rm_batch_code} />
          <Field label="Supplier" value={rm.supplier_name} />
          <Field label="Receipt Date" value={rm.date} />
          <Field label="MTC / Heat No" value={rm.mtc_number} />
          <Field label="Grade" value={rm.grade} />
          <Field
            label="Spec"
            value={
              rm.thickness_mm || rm.width_mm
                ? `${rm.thickness_mm || "?"} × ${rm.width_mm || "?"} mm`
                : ""
            }
          />
        </div>
      </div>

      {/* Loading codes */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3">Lineage Codes</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="First-Op Loading" value={data.first_op_loading} />
          <Field label="First-Op Date" value={data.first_op_close_date} />
          <Field label="Last-Op Loading" value={data.last_op_loading} />
          <Field label="Last-Op Date" value={data.last_op_close_date} />
        </div>
      </div>

      {/* Op chain */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3">
          Operation Chain ({data.history.length} step{data.history.length === 1 ? "" : "s"})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="public-trace-history-table">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Op #</th>
                <th className="px-3 py-2 text-left font-semibold">Tool</th>
                <th className="px-3 py-2 text-left font-semibold">Machine</th>
                <th className="px-3 py-2 text-left font-semibold">Operator</th>
                <th className="px-3 py-2 text-right font-semibold">Qty</th>
                <th className="px-3 py-2 text-left font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((h, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold text-[#1A3560]">{h.op_seq}</td>
                  <td className="px-3 py-2">{h.tool_no || "—"}</td>
                  <td className="px-3 py-2">{h.machine || "—"}</td>
                  <td className="px-3 py-2">{h.operator || "—"}</td>
                  <td className="px-3 py-2 text-right">{h.qty}</td>
                  <td className="px-3 py-2">{h.date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
